import React, { useMemo, useState } from "react";

import { CategoryChart, LongText } from "../processors";
import { SupplyDemandKpiProvider, SupplyDemandKpiProviderBase } from "../categoryChartQueryFns";
import { Paragraph } from "../Paragraph";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "../misc";
import {
    SpatialValue,
    // SupplyDemandMapDemo,
    SupplyDemandMapMetadata,
    SupplyDemandProviderMap,
    SupplyDemandProviderMapLegend,
    SupplyDemandProviderMapProps
} from "../../Components/Map";
import _ from "lodash";

import styles from "./SupplyDemandChart.module.scss";

interface SupplyDemandChartProps extends SupplyDemandKpiProvider {
    categoryChart: CategoryChart;
}
export interface ToggleButtonProps {
    className?: string;
    buttons: { label: string; onClick?: React.MouseEventHandler<HTMLButtonElement>; isActive: boolean }[];
}

export const ToggleButtons: React.FC<ToggleButtonProps> = ({ buttons, className }) => {
    return (
        <div className={`${styles.toogleButtonContainer} ${className ?? ""}`}>
            {buttons?.map((d, index) => {
                return (
                    <button className={`${d.isActive ? "active" : ""}`} key={index} onClick={d.onClick}>
                        {d.label}
                    </button>
                );
            })}
        </div>
    );
};

export const SupplyDemandChart: React.FC<SupplyDemandChartProps> = ({ title, footer, vis }) => {
    // const { category } = useCategoryContext();

    // if (!category) return null;

    // console.log("title", "footer", "vis", title, footer, vis);

    const { data, waterProvider, header } =
        vis ??
        (EMPTY_OBJECT as SupplyDemandKpiProviderBase & {
            header: LongText;
            footer: LongText;
        });

    const kpiCodes = useMemo(() => {
        const kpis = _.uniqBy(data, d => d.kpiCode);
        return [kpis.find(d => d.shortLabel === "18 months"), kpis.find(d => d.shortLabel === "5 years")].filter(
            d => d != null
        );
    }, [data]);

    const [selectedKpiCode, setSelectedKpiCode] = useState(kpiCodes?.[0]?.kpiCode);

    const toggleButtonProps: ToggleButtonProps = useMemo(() => {
        const buttons: { label: string; isActive: boolean; onClick?: React.MouseEventHandler<HTMLButtonElement> }[] =
            kpiCodes?.map(d => {
                return {
                    label: d.shortLabel,
                    isActive: d.kpiCode === selectedKpiCode,
                    onClick: ___ => {
                        setSelectedKpiCode(d.kpiCode);
                    }
                };
            });

        return {
            buttons
        };
    }, [kpiCodes, selectedKpiCode, setSelectedKpiCode]);

    const mapProps: SupplyDemandProviderMapProps = useMemo(() => {
        const providerName = waterProvider?.name;
        const providerMapName = waterProvider?.systemName;
        const providerAdminId = waterProvider?.adminId;
        const selectedData = data?.filter(d => d.kpiCode === selectedKpiCode);
        const spatialValues: SpatialValue[] = [
            ...(selectedData?.map(d => {
                return {
                    adminId: d.entityId,
                    name: d.entity,
                    category: d.value
                };
            }) ?? (EMPTY_ARRAY as SpatialValue[])),
            {
                adminId: providerAdminId,
                category: "providerGeometry",
                name: providerName
            }
        ];

        const metadata: SupplyDemandMapMetadata = {
            providerName,
            schemeMetadata:
                selectedData?.map(d => {
                    return {
                        longLabel: d.longLabel,
                        schemeName: d.entity,
                        schemeId: d.entityId,
                        comments: d.comments
                    };
                }) ?? EMPTY_ARRAY
        };

        return {
            metadata,
            providerAdminId,
            providerName,
            providerMapName,
            spatialValues
        };
    }, [selectedKpiCode, data, waterProvider]);

    return (
        <div className="row">
            {title != null && title?.processed?.trim() !== "" && (
                <>
                    <Paragraph
                        title={""}
                        status={true}
                        nid={0}
                        text={title}
                        footer={
                            header != null && (header?.value ?? "")?.trim() !== ""
                                ? {
                                      value: `<span class="ml-2">${header?.value}</span>`,
                                      processed: `<span class="ml-2">${header?.value}</span>`,
                                      format: ""
                                  }
                                : null
                        }
                        className={`col-12 headline ${styles.headline}`}
                    >
                        {kpiCodes?.length > 1 && (
                            <ToggleButtons {...toggleButtonProps} className={"ml-2"}></ToggleButtons>
                        )}
                        {kpiCodes?.length === 1 && kpiCodes?.[0]?.shortLabel}
                    </Paragraph>
                </>
            )}

            {(title == null || title?.processed?.trim() === "") && (
                <div className="col-12">
                    {kpiCodes?.length > 1 && <ToggleButtons {...toggleButtonProps} className={"ml-2"}></ToggleButtons>}
                    {kpiCodes?.length === 1 && kpiCodes?.[0]?.shortLabel}
                </div>
            )}

            <div className="col-12">
                <div className="row">
                    <div className={styles.mapLegend}>
                        <SupplyDemandProviderMapLegend />
                    </div>
                    <div className={styles.mapContainer}>
                        <SupplyDemandProviderMap {...mapProps} />
                        {/* <SupplyDemandMapDemo /> */}
                    </div>
                </div>
            </div>

            {footer != null && <Paragraph title={""} status={true} nid={0} text={footer} className="col-12" />}
        </div>
    );
};
